import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    oneCheckoutBtnRenderer.hide1CTarget({
      target: 'cart_button',
      selector: '.cart-drawer__summary #checkout',
      observeUrl: '/cart/add',
      observeForBtn: {
        container: 'body',
        nodeName: 'FORM'
      },
      dynamicBtn: `
        <button id="buyNow1CheckoutBtn" type="button" class="btn-1checkout btn-1checkout-style" onclick="closeCartDrawerViaTheme();oneCheckout.trigger1Checkout(this)">
          <span class="btn-1checkout-title">Check out</span>
          <span class="btn-1checkout-spinner dc-none"></span>
        </button>`
    });

    oneCheckoutBtnRenderer.render1CBtn({
      target: 'cart_button',
      selector: '.cart-drawer__summary #checkout',
      observeUrl: '/cart/add',
      observeForBtn: {
        container: 'body',
        nodeName: 'FORM'
      },
      dynamicBtn: `
        <button id="buyNow1CheckoutBtn" type="button" class="btn-1checkout btn-1checkout-style" onclick="closeCartDrawerViaTheme();oneCheckout.trigger1Checkout(this)">
          <span class="btn-1checkout-title">Check out</span>
          <span class="btn-1checkout-spinner dc-none"></span>
        </button>`
    });
    
    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
